import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProjectsAPI } from '../hooks/useProjectsAPI';
import { useEntitlements } from '../context/EntitlementsContext';
import AppHeader from '../components/AppHeader';
import BottomNav from '../components/BottomNav';
import { Plus, House as HouseIcon, Warning, CheckCircle, Trash, LockOpen, ShareFat } from '@phosphor-icons/react';
import { toast } from 'sonner';
import EmptyState from '../components/EmptyState';
import InstallHintCard from '../components/InstallHintCard';
import DeveloperNameInput from '../components/DeveloperNameInput';

export default function Projects() {
    const { user } = useAuth();
    const store = useProjectsAPI(!!user?.user_id);
    const ent = useEntitlements();
    const [showNew, setShowNew] = useState(false);
    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [developerName, setDeveloperName] = useState('');
    const navigate = useNavigate();
    // Tracks whether the post-unlock referral toast has been shown this
    // browser session, so it doesn't re-fire every render. We DON'T
    // persist this — refreshing the page once a week is fine for a
    // gentle nudge that's only valid for ~30 days post-unlock anyway.
    const referralToastShown = useRef(false);

    /** Post-unlock referral nudge — see comment above the original
     *  useEffect for full rationale. Hoisted ABOVE the `if (!store.ready)`
     *  early return to comply with React's Rules of Hooks (same lesson
     *  we learned with editingMeta in ProjectDetail.jsx — don't ever
     *  put hooks after early returns). */
    const projectsForReferral = store.state.projects;
    useEffect(() => {
        if (referralToastShown.current) return;
        if (!ent?.tier || ent.tier !== 'rectify') return;
        try {
            const lastDismiss = localStorage.getItem('defectr:referral-dismissed-at');
            if (lastDismiss) {
                const since = Date.now() - parseInt(lastDismiss, 10);
                if (since < 30 * 24 * 60 * 60 * 1000) return;
            }
        } catch { /* localStorage blocked — fail soft */ }

        const REFERRAL_MIN_MS = 24 * 60 * 60 * 1000;
        const REFERRAL_MAX_MS = 30 * 24 * 60 * 60 * 1000;
        const sweetSpot = (projectsForReferral || []).some(p => {
            if (!p.is_unlocked || !p.unlocked_at) return false;
            const stamp = Date.parse(p.unlocked_at);
            if (Number.isNaN(stamp)) return false;
            const age = Date.now() - stamp;
            return age >= REFERRAL_MIN_MS && age <= REFERRAL_MAX_MS;
        });
        if (!sweetSpot) return;

        referralToastShown.current = true;
        toast('💌 Loving DefectR?', {
            description: 'Tell a friend snagging their new build — give them a heads-up the tool exists.',
            duration: 12000,
            action: {
                label: 'Share',
                onClick: async () => {
                    const msg = `I've been using DefectR to log defects in my new build — proper photo evidence and a shareable report for the builder. Check it out: https://defectr.co.uk`;
                    try {
                        if (navigator.share) {
                            await navigator.share({ title: 'DefectR — Snag your new build', text: msg, url: 'https://defectr.co.uk' });
                        } else {
                            await navigator.clipboard.writeText(msg);
                            toast.success('Share text copied — paste it anywhere.');
                        }
                    } catch { /* user cancelled — silent */ }
                },
            },
            cancel: {
                label: 'Not now',
                onClick: () => {
                    try { localStorage.setItem('defectr:referral-dismissed-at', String(Date.now())); } catch { /* ignore */ }
                },
            },
        });
    }, [projectsForReferral, ent?.tier]);

    if (!store.ready) return null;
    const { projects } = store.state;

    const counts = (p) => {
        const out = p.snags.filter(s => s.status === 'outstanding').length;
        const done = p.snags.filter(s => s.status === 'completed').length;
        return { out, done };
    };

    /** True if the property was unlocked within the last 24h. Drives the
     *  tiny "🔓 Just unlocked" badge — a small celebratory cue right at
     *  the moment Foundation users cross into Rectify, without being
     *  loud or persistent. Stamped from the server's `unlocked_at`, so
     *  it survives reloads and follows the user across devices. */
    const JUST_UNLOCKED_WINDOW_MS = 24 * 60 * 60 * 1000;
    const isJustUnlocked = (p) => {
        if (!p.is_unlocked || !p.unlocked_at) return false;
        const stamp = Date.parse(p.unlocked_at);
        if (Number.isNaN(stamp)) return false;
        return Date.now() - stamp < JUST_UNLOCKED_WINDOW_MS;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        const p = await store.createProject(name.trim(), address.trim(), developerName.trim());
        if (!p) return; // hook surfaced a toast on failure (e.g., 402)
        setShowNew(false); setName(''); setAddress(''); setDeveloperName('');
        navigate(`/app/project/${p.id}`);
    };

    return (
        <div className="min-h-screen bg-white pb-24">
            <AppHeader title={`Hi, ${user?.name?.split(' ')[0] || 'there'}`} subtitle="Your properties" />

            <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
                <InstallHintCard />
                <div className="flex items-end justify-between mb-4">
                    <div>
                        <div className="label-tiny">Properties</div>
                        <h2 className="font-display text-2xl font-semibold">Defect logs</h2>
                    </div>
                    <button
                        onClick={() => setShowNew(true)}
                        data-testid="new-project-btn"
                        className="inline-flex items-center gap-2 bg-[#FF5500] text-white px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#1787AB] active:scale-95 transition-all duration-150"
                    >
                        <Plus size={16} weight="bold" /> New
                    </button>
                </div>

                {projects.length === 0 && !showNew && (
                    <EmptyState
                        icon={HouseIcon}
                        eyebrow="Welcome to DefectR"
                        title="Add your first property"
                        body="Register your new build to start logging defects, room by room. Everything is saved automatically."
                        cta={{
                            label: 'Add property',
                            icon: Plus,
                            onClick: () => setShowNew(true),
                            testId: 'empty-new-project-btn',
                        }}
                        testId="empty-projects"
                    />
                )}

                {showNew && (
                    <form onSubmit={submit} className="grid-border p-5 mb-6 fade-up bg-white" data-testid="new-project-form">
                        <div className="label-tiny mb-3">New property</div>
                        <input
                            data-testid="project-name-input"
                            autoFocus
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Plot 14, Oak Drive"
                            className="w-full border border-[#E4E4E7] focus:border-[#18181B] outline-none px-3 py-3 font-display text-lg"
                        />
                        <input
                            data-testid="project-address-input"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            placeholder="Full address (optional)"
                            className="w-full mt-3 border border-[#E4E4E7] focus:border-[#18181B] outline-none px-3 py-3 text-sm"
                        />
                        <div className="mt-3">
                            <DeveloperNameInput
                                value={developerName}
                                onChange={setDeveloperName}
                                testId="project-developer-input"
                            />
                            <p className="mt-1 text-[10px] font-mono uppercase tracking-[0.18em] text-[#A1A1AA]">
                                Optional — helps us spot patterns across homes by the same housebuilder
                            </p>
                        </div>
                        <div className="flex gap-2 mt-4">
                            <button
                                type="submit"
                                data-testid="submit-project-btn"
                                className="flex-1 bg-[#FF5500] text-white py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#1787AB] active:scale-95 transition-all duration-150"
                            >Create</button>
                            <button
                                type="button"
                                onClick={() => { setShowNew(false); setName(''); setAddress(''); setDeveloperName(''); }}
                                className="px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] border border-[#18181B] hover:bg-[#F4F4F5] active:scale-95 transition-all duration-150"
                            >Cancel</button>
                        </div>
                    </form>
                )}

                <ul className="divide-y divide-[#E4E4E7] border border-[#E4E4E7]">
                    {projects.map((p, i) => {
                        const c = counts(p);
                        return (
                            <li key={p.id} className="fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                                <button
                                    onClick={() => navigate(`/app/project/${p.id}`)}
                                    data-testid={`project-card-${p.id}`}
                                    className="w-full text-left p-4 hover:bg-[#F4F4F5] transition-all duration-150 flex items-center gap-4"
                                >
                                    <div className="w-12 h-12 bg-[#09090B] text-white flex items-center justify-center font-mono font-bold">
                                        {p.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-display font-semibold truncate flex items-center gap-2">
                                            <span className="truncate">{p.name}</span>
                                            {isJustUnlocked(p) && (
                                                <span
                                                    data-testid={`just-unlocked-badge-${p.id}`}
                                                    title="Welcome to Rectify — unlocked within the last 24h"
                                                    className="inline-flex items-center gap-1 bg-[#E8F5FB] text-[#1787AB] border border-[#1FA5CF] font-mono text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 flex-none"
                                                >
                                                    <LockOpen size={10} weight="bold" /> Just unlocked
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-[#52525B] truncate">{p.address || 'No address set'}</div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="inline-flex items-center gap-1 bg-[#E53935] text-white font-mono text-[10px] uppercase tracking-wider px-2 py-1">
                                            <Warning size={12} weight="fill" /> {c.out}
                                        </span>
                                        <span className="inline-flex items-center gap-1 bg-[#00875A] text-white font-mono text-[10px] uppercase tracking-wider px-2 py-1">
                                            <CheckCircle size={12} weight="fill" /> {c.done}
                                        </span>
                                    </div>
                                </button>
                                <div className="px-4 pb-3 flex justify-end">
                                    <button
                                        onClick={() => {
                                            const t = toast(`Delete "${p.name}"?`, {
                                                description: 'This removes all rooms, defects, photos and floorplans on this property. Cannot be undone.',
                                                duration: 10000,
                                                action: {
                                                    label: 'Delete',
                                                    onClick: () => store.deleteProject(p.id),
                                                },
                                                cancel: { label: 'Cancel', onClick: () => toast.dismiss(t) },
                                            });
                                        }}
                                        data-testid={`delete-project-${p.id}`}
                                        className="text-xs text-[#52525B] hover:text-[#E53935] inline-flex items-center gap-1"
                                    >
                                        <Trash size={12} /> Delete
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </main>

            <BottomNav />
        </div>
    );
}
